import React, { useState } from 'react';
import { Box, Avatar, Tooltip, Typography } from '@mui/material';
import { Route as RouteIcon } from '@mui/icons-material';
import { ShowChart as ElevationIcon } from '@mui/icons-material';
import { getRouteThumbnailUrl } from '../../utils/thumbnailGenerator';
import { getElevationThumbnailUrl } from '../../utils/elevationThumbnailGenerator';
import type { Route } from '../../types/storage';

interface ThumbnailsDisplayProps {
  route: Route;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  onThumbnailClick?: (route: Route, type: 'route' | 'elevation') => void;
  showLabels?: boolean;
  layout?: 'horizontal' | 'vertical';
  showElevationOnly?: boolean;
}

export const ThumbnailsDisplay: React.FC<ThumbnailsDisplayProps> = React.memo(({
  route,
  size = 'medium',
  onThumbnailClick,
  showLabels = false,
  layout = 'horizontal',
  showElevationOnly = false
}) => {
  const [routeThumbnailError, setRouteThumbnailError] = useState(false);
  const [elevationThumbnailError, setElevationThumbnailError] = useState(false);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';

  // Get thumbnail URLs
  const routeThumbnailUrl = route.thumbnail_path && !routeThumbnailError && route.thumbnail_path.trim() !== ''
    ? getRouteThumbnailUrl(supabaseUrl, route.thumbnail_path, size === 'small' ? 'small' : 'medium')
    : null;

  const elevationThumbnailUrl = route.elevation_thumbnail_path && !elevationThumbnailError && route.elevation_thumbnail_path.trim() !== ''
    ? getElevationThumbnailUrl(supabaseUrl, route.elevation_thumbnail_path, size === 'small' ? 'small' : 'medium')
    : null;

  // Check if route has elevation data (even without stored thumbnail)
  const hasElevationData = route.elevation_thumbnail_path || (route.ascend && route.ascend > 0);

  // Size configurations - separate for route and elevation thumbnails
  const routeThumbnailSizeConfig = {
    small: { width: 80, height: 60 },    // Original route thumbnail sizes
    medium: { width: 120, height: 90 },  // Original route thumbnail sizes
    large: { width: 160, height: 120 },   // Original route thumbnail sizes
    xlarge: { width: 200, height: 150 }   // Extra large route thumbnail sizes
  };

  const elevationThumbnailSizeConfig = {
    small: { width: 140, height: 70 },   // Display size for 400x200 thumbnails (scaled down, 2:1)
    medium: { width: 180, height: 90 },  // Display size for 600x300 thumbnails (scaled down, 2:1)
    large: { width: 240, height: 120 },   // Display size for 800x400 thumbnails (scaled down, 2:1)
    xlarge: { width: 320, height: 160 }   // Extra large display size for elevation thumbnails (2:1)
  };

  // Use appropriate size config based on thumbnail type
  const { width, height } = showElevationOnly 
    ? elevationThumbnailSizeConfig[size]
    : routeThumbnailSizeConfig[size];
  const gap = size === 'small' ? 1 : 1.5;

  // If no thumbnails available, show nothing
  if ((!routeThumbnailUrl && !elevationThumbnailUrl && !hasElevationData) || 
      (showElevationOnly && !elevationThumbnailUrl && !hasElevationData)) {
    return null;
  }

  const ThumbnailAvatar: React.FC<{
    url: string | null;
    icon: React.ReactNode;
    label: string;
    onError: () => void;
    type: 'route' | 'elevation';
  }> = ({ url, icon, label, onError, type }) => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={0.5}
    >
      <Tooltip title={url ? `View ${label} thumbnail` : `${label} not available`} arrow>
        <Avatar
          src={url || undefined}
          variant="rounded"
          onClick={url && onThumbnailClick ? (e) => {
            e.stopPropagation();
            onThumbnailClick(route, type);
          } : undefined}
          sx={{
            width,
            height,
            bgcolor: 'action.hover',
            cursor: url && onThumbnailClick ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            opacity: url ? 1 : 0.6,
            '&:hover': url && onThumbnailClick ? {
              transform: 'scale(1.02)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            } : {},
            '& img': {
              objectFit: 'cover'
            }
          }}
          imgProps={{
            onError
          }}
        >
          {icon}
        </Avatar>
      </Tooltip>
      {showLabels && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: 'text.secondary',
            textAlign: 'center',
            maxWidth: width,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection={layout === 'horizontal' ? 'row' : 'column'}
      alignItems="center"
      gap={gap}
      sx={{ flexShrink: 0 }}
    >
      {/* Route Thumbnail - always show unless specifically elevation-only */}
      {!showElevationOnly && (
        <ThumbnailAvatar
          url={routeThumbnailUrl}
          icon={<RouteIcon sx={{ fontSize: size === 'small' ? 18 : 24, color: 'text.secondary' }} />}
          label="Route Map"
          onError={() => setRouteThumbnailError(true)}
          type="route"
        />
      )}

      {/* Elevation Thumbnail - show in elevation-only mode when we have elevation data */}
      {showElevationOnly && hasElevationData && (
        <ThumbnailAvatar
          url={elevationThumbnailUrl}
          icon={<ElevationIcon sx={{ fontSize: size === 'small' ? 18 : 24, color: 'text.secondary' }} />}
          label="Elevation Profile"
          onError={() => setElevationThumbnailError(true)}
          type="elevation"
        />
      )}
    </Box>
  );
});
